import { Link } from 'react-router-dom';
import type { BatchSummary } from '../types';
import { StatusBadge } from './StatusBadge';

interface BatchCardProps {
  batch: BatchSummary;
}

export function BatchCard({ batch }: BatchCardProps) {
  const progressPercent =
    batch.totalOrders > 0
      ? Math.round((batch.resolvedOrders / batch.totalOrders) * 100)
      : 0;

  const formattedDate = new Date(batch.date + 'T00:00:00').toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });

  return (
    <Link
      to={`/batch/${batch.date}`}
      className="block bg-white rounded-lg shadow-sm border border-gray-200 p-4 hover:shadow-md hover:border-gray-300 transition-all"
    >
      <div className="flex items-center justify-between mb-3">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">{formattedDate}</h3>
          {batch.assignedTo && (
            <p className="text-sm text-gray-500">{batch.assignedTo}</p>
          )}
        </div>
        <StatusBadge status={batch.status} />
      </div>

      <div className="space-y-2">
        <div className="flex justify-between text-sm">
          <span className="text-gray-600">Progress</span>
          <span className="font-medium text-gray-900">
            {batch.resolvedOrders} / {batch.totalOrders} orders
          </span>
        </div>

        <div className="w-full bg-gray-200 rounded-full h-2">
          <div
            className={`h-2 rounded-full transition-all ${
              batch.status === 'complete'
                ? 'bg-green-500'
                : batch.status === 'partial'
                  ? 'bg-blue-500'
                  : 'bg-yellow-500'
            }`}
            style={{ width: `${progressPercent}%` }}
          />
        </div>

        <div className="flex justify-between text-sm pt-1">
          <div className="flex gap-2">
            {(batch.actionRequiredOrders || 0) > 0 && (
              <span className="text-red-600 font-medium flex items-center gap-1">
                <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M8.485 2.495c.673-1.167 2.357-1.167 3.03 0l6.28 10.875c.673 1.167-.17 2.625-1.516 2.625H3.72c-1.345 0-2.189-1.458-1.515-2.625L8.485 2.495zM10 6a.75.75 0 01.75.75v3.5a.75.75 0 01-1.5 0v-3.5A.75.75 0 0110 6zm0 9a1 1 0 100-2 1 1 0 000 2z" clipRule="evenodd" />
                </svg>
                {batch.actionRequiredOrders} need action
              </span>
            )}
            <span className={batch.pendingOrders - (batch.actionRequiredOrders || 0) > 0 ? 'text-yellow-600' : 'text-gray-500'}>
              {batch.pendingOrders - (batch.actionRequiredOrders || 0)} pending
            </span>
          </div>
          <span className="text-gray-500">{progressPercent}% complete</span>
        </div>
      </div>
    </Link>
  );
}
